/* eslint-disable max-lines */

import gql from 'graphql-tag';

export const addressFields = gql`
  fragment AddressFields on Address {
    city
    country
    line1
    line2
    zip
    state
  }
`;

export const pibisiFlagFields = gql`
  fragment PibisiFlagFields on PibisiFlag {
    id
    content {
      action
      charge
      country
      from
      issue
      issuer
      organization
      reason
      scope
      to
      types
      url
    }
  }
`;

export const pibisiFields = gql`
  fragment PibisiFields on Pibisi {
    id
    entityId
    status
    createdAt
    updatedAt
    deletedAt
  }
`;

export const pibisiAccountFields = gql`
  fragment PibisiAccountFields on PibisiAccount {
    id
    entityId
    createdAt
    updatedAt
    deletedAt
    deleted
    status
    customerId
    legalName
    alertsId
    alertsStatus
    matches {
      id
      status
      adverseInfo {
        ...PibisiFlagFields
      }
      highRisk {
        ...PibisiFlagFields
      }
      media {
        ...PibisiFlagFields
      }
      pep {
        ...PibisiFlagFields
      }
      sanctioned {
        ...PibisiFlagFields
      }
      terrorist {
        ...PibisiFlagFields
      }
      matches {
        ...PibisiFlagFields
      }
    }
    alerts {
      id
      status
      type
      note
    }
  }
  ${pibisiFlagFields}
`;

export const sessionDetailsFields = gql`
  fragment SessionDetailsFields on SessionDetails {
    ip
    fingerprint
    userAgent
    countryCode
    lang
    deviceType
    deviceModel
    browser
    browserVersion
    browserAccept
    browserColorDepth
    browserScreenHeight
    browserScreenWidth
    browserTimezoneOffset
    os
    osVersion
    source
    sourceVersion
  }
`;

export const traceDetailsFields = gql`
  fragment TraceDetailsFields on TraceDetails {
    ip
    userAgent
    countryCode
    lang
    deviceType
    deviceModel
    browser
    browserVersion
    browserAccept
    os
    osVersion
    source
    sourceVersion
    userId
    userEmail
    userName
  }
`;

export const paymentMethodFields = gql`
  fragment PaymentMethodFields on PaymentMethod {
    method
    bizum {
      phoneNumber
      integrationType
      ibanLast4
      idBizum
    }
    card {
      brand
      country
      type
      threeDSecure
      threeDSecureVersion
      threeDSecureFlow
      threeDSecureStatus
      last4
      fingerprint
      cardholderName
      cardholderEmail
      expiration
      bank
      tokenizationMethod
      srcCorrelationId
      srciTransactionId
      srcDpaId
    }
    cardPresent {
      bin
      brand
      cardholderEmail
      cardholderName
      country
      expiration
      last4
      fingerprint
      type
      verificationMethod
    }
    mbway {
      phoneNumber
    }
    paypal {
      orderId
      payerId
      email
      name
    }
    sepa {
      accountholderAddress {
        city
        country
        line1
        line2
        zip
        state
      }
      accountholderEmail
      accountholderName
      countryCode
      bankAddress
      bankCode
      bankName
      bic
      last4
      fingerprint
    }
    trustly {
      customerId
    }
    klarna {
      billingCategory
      authPaymentMethod
    }
  }
`;

export const providerFields = gql`
  fragment ProviderFields on Provider {
    id
    processor
    enabled
    test
    paymentMethod
    acquirer
    currencies
    metadata {
      key
      value
      type
      label
      possibleValues
      regex
      example
      secret
    }
    rules {
      minAmount
      maxAmount
      shopNameLike
      descriptionLike
    }
    nativeHostedPayment
  }
`;

export const chargeFields = gql`
  fragment ChargeFields on Charge {
    id
    accountId
    providerId
    checkoutId
    sequenceId
    processor
    subscriptionId
    pointOfSaleId
    providerReferenceId
    providerInternalId
    masterAccountId
    billingPlan
    createdAt
    updatedAt
    amount
    acquirer
    authorizationCode
    storeId
    billingDetails {
      email
      name
      company
      phone
      taxId
      address {
        ...AddressFields
      }
    }
    currency
    customer {
      email
      name
      phone
    }
    description
    descriptor
    livemode
    orderId
    paymentMethod {
      ...PaymentMethodFields
    }
    refundedAmount
    lastRefundAmount
    lastRefundReason
    cancellationReason
    shippingDetails {
      email
      name
      company
      phone
      taxId
      address {
        ...AddressFields
      }
    }
    shop {
      name
      country
    }
    status
    statusCode
    statusMessage
    providerStatusCode
    providerStatusMessage
    issuerStatusCode
    issuerStatusMessage
    sessionDetails {
      ...SessionDetailsFields
    }
    traceDetails {
      ...TraceDetailsFields
    }
    metadata {
      key
      value
    }
    fraudDetectorScore
    callbackUrl
    completeUrl
    failUrl
    cancelUrl
  }
  ${addressFields}
  ${sessionDetailsFields}
  ${traceDetailsFields}
  ${paymentMethodFields}
`;

export const chargeEventObjectFields = gql`
  fragment ChargeEventObjectFields on ChargeEventObject {
    id
    accountId
    providerId
    checkoutId
    sequenceId
    billingPlan
    subscriptionId
    storeId
    pointOfSaleId
    providerReferenceId
    providerInternalId
    createdAt
    updatedAt
    amount
    amountEUR
    authorizationCode
    billingDetails {
      email
      name
      company
      phone
      taxId
      address {
        ...AddressFields
      }
    }
    currency
    customer {
      email
      name
      phone
    }
    description
    descriptor
    livemode
    orderId
    terminalId
    masterAccountId
    paymentMethod {
      ...PaymentMethodFields
    }
    refundedAmount
    lastRefundAmount
    lastRefundReason
    cancellationReason
    shippingDetails {
      email
      name
      company
      taxId
      phone
      address {
        ...AddressFields
      }
    }
    shop {
      name
      country
    }
    status
    statusCode
    statusMessage
    providerStatusCode
    providerStatusMessage
    issuerStatusCode
    issuerStatusMessage
    sessionDetails {
      ...SessionDetailsFields
    }
    traceDetails {
      ...TraceDetailsFields
    }
    pageOpenedAt
    metadata {
      key
      value
    }
    fraudDetectorScore
    callbackUrl
    completeUrl
    failUrl
    cancelUrl
  }
  ${addressFields}
  ${sessionDetailsFields}
  ${traceDetailsFields}
  ${paymentMethodFields}
`;

export const userFields = gql`
  fragment UserFields on User {
    id
    email
    emailVerified
    phoneNumber
    phoneNumberVerified
    smsMfaEnabled
    status
    enabled
    accountId
    testAccountId
    referralCode
    promoCode
    createdAt
  }
`;

export const accountFields = gql`
  fragment AccountFields on Account {
    id
    refAccountId
    testAccountId
    prodAccountId
    name
    status
    note
    userNote
    partner
    acquirer
    adminEmail
    allowCreateApiKey
    allowRefunds
    defaultCurrency
    defaultTransactionType
    referralCode
    promoCode
    language
    reviewConfirmed
    progressiveBilling
    contactPerson {
      name
      email
      phone
    }
    branding {
      accentColor
      bgColor
      logoUrl
      iconUrl
    }
    tokenizationMethods {
      googlePay {
        enabled
      }
      applePay {
        enabled
      }
      clickToPay {
        enabled
      }
    }
    billingEmail
    notifications {
      emailCustomerOnRefund
      emailCustomerOnSuccess
      emailAdminOnSuccess
      emailCustomerOnSubscription
      emailAdminOnSubscription
      emailOnAccountInvoice
    }
    billingPlan
    fees {
      highRiskFee
      dailyExtraSeatsFee
      dailyPlatformFee
      weeklyCustomDomainFee
      fraudDetectorTrxFlatFee
      cardPresentTrxFee
      cardPresentTrxFlatFee
      steps {
        trxFee
        bizumTrxFee
        bizumTrxFlatFee
        trxFlatFee
        from
      }
      extra {
        name
        amount
        type
        masterAccountFee
        paymentMethods
      }
    }
    riskScore
    riskProfile
    riskProcessing
    riskProfileNote
    riskIgnoredKeywords
    riskUpdatedAt
    riskLabels {
      label
      category
      riskScore
    }
    business {
      country
      legalForm
      legalName
      companyName
      mcc
      address {
        ...AddressFields
      }
      documentNumber
      status
      email
      phone
      website
      monthlyVolume
      confirmWebsite
      industry
      mcc
      servicesDescription
      confirmServicesDescription
      legalAddress
      legalDescription
      creationDate
      lastUpdateDate
      startActivityDate
      registryRecords {
        name
        url
        province
        date
      }
      positions {
        fromDate
        role
        name
      }
      capital
      previousNames
      verificationAttempts
      pibisi {
        ...PibisiFields
      }
    }
    representatives {
      firstName
      lastName
      address {
        ...AddressFields
      }
      email
      phone
      documentNumber
      stocksPercentage
      gender
      citizenship
      dateOfBirth
      nationality
      citizenship
      yearOfBirth
      placeOfBirth
      document {
        type
        number
        country
        validFrom
        validUntil
      }
      idSession
      interventionType
      status
      statusCode
      hasNoPublicActivity
      riskLabels {
        label
        category
      }
      pibisi {
        ...PibisiFields
      }
    }
    iban
    dynamicBilling
    payoutTerms {
      iban
      period
      firstPeriod
      reservePercent
      suspended
      releaseReserve
      settleGross
      bankTransferConceptPrefix
      settleToStores
    }
    publicBusinessDetails {
      companyName
      website
      phone
      address {
        ...AddressFields
      }
      supportWebsite
      supportPhone
      supportEmail
    }
    branding {
      accentColor
      bgColor
      logoUrl
      iconUrl
    }
    test
    secret
    apiKey
    customDomain
    createdAt
    updatedAt
    deletedAt
    approvedAt
    rejectedAt
    activatedAt
    sepblacId
    approvalRequestedAt
    suspendedAt
    dataUpdatedAt
    contract {
      envelopeId
      status
      path
      signedDate
    }
    accountExecutive
    upsell {
      executive
      paymentMethods
      date
      note
    }
    billingVatPercent
    churned
    blocked
    statusReason
    statusNote
    churnReason
    churnReasonOption
    churnNote
    previewFeatures
    tags
    masterAccountId
    accountInvoicePaymentMethod
    customAccountInvoiceNotes
    groupInvoicesByStore
    fraudDetectorEnabled
    fraudDetectorThreshold
    fraudRulesEnabled
  }
  ${addressFields}
  ${pibisiFields}
`;

export const authorizedUserFields = gql`
  fragment AuthorizedUserFields on AuthorizedUser {
    id
    accountId
    userEmail
    accessLevel
    storeId
    pointOfSaleId
    status
    createdAt
    updatedAt
    lastLoginAt
  }
`;

export const accountEventObjectFields = gql`
  fragment AccountEventObjectFields on AccountEventObject {
    id
    refAccountId
    name
    status
    note
    userNote
    partner
    acquirer
    adminEmail
    defaultCurrency
    tags
    defaultTransactionType
    referralCode
    promoCode
    reviewConfirmed
    tokenizationMethods {
      googlePay {
        enabled
      }
      applePay {
        enabled
      }
      clickToPay {
        enabled
      }
    }
    notifications {
      emailCustomerOnRefund
      emailCustomerOnSuccess
      emailAdminOnSuccess
    }
    billingPlan
    fees {
      highRiskFee
      dailyExtraSeatsFee
      dailyPlatformFee
      weeklyCustomDomainFee
      fraudDetectorTrxFlatFee
      cardPresentTrxFee
      cardPresentTrxFlatFee
      steps {
        trxFee
        bizumTrxFee
        bizumTrxFlatFee
        trxFlatFee
        from
      }
      extra {
        name
        amount
        type
        paymentMethods
        region
      }
    }
    riskScore
    business {
      country
      legalForm
      legalName
      companyName
      mcc
      address {
        ...AddressFields
      }
      documentNumber
      status
      email
      phone
      website
      monthlyVolume
      confirmWebsite
      industry
      mcc
      servicesDescription
      confirmServicesDescription
      legalAddress
      legalDescription
      creationDate
      lastUpdateDate
      startActivityDate
      registryRecords {
        name
        url
        province
        date
      }
      positions {
        fromDate
        role
        name
      }
      capital
      previousNames
      verificationAttempts
    }
    representatives {
      firstName
      lastName
      address {
        ...AddressFields
      }
      email
      phone
      documentNumber
      stocksPercentage
      gender
      citizenship
      dateOfBirth
      nationality
      yearOfBirth
      placeOfBirth
      document {
        type
        number
        country
        validFrom
        validUntil
      }
      idSession
      interventionType
      status
      statusCode
      hasNoPublicActivity
      riskLabels {
        label
        category
      }
    }
    iban
    dynamicBilling
    payoutTerms {
      iban
      period
      firstPeriod
      reservePercent
      suspended
      releaseReserve
    }
    publicBusinessDetails {
      companyName
      website
      phone
      address {
        ...AddressFields
      }
      supportWebsite
      supportPhone
      supportEmail
    }
    branding {
      accentColor
      bgColor
      logoUrl
      iconUrl
    }
    test
    customDomain
    contract {
      envelopeId
      status
      path
      signedDate
    }
    createdAt
    updatedAt
    deletedAt
    approvedAt
    rejectedAt
    activatedAt
    approvalRequestedAt
    riskScore
    riskProfile
    riskProfileNote
    riskLabels {
      label
      category
      riskScore
    }
    riskIgnoredKeywords
    riskUpdatedAt
    suspendedAt
    dataUpdatedAt
    fraudDetectorEnabled
    fraudDetectorThreshold
    fraudRulesEnabled
    traceDetails {
      ...TraceDetailsFields
    }
    churnReason
    churnReasonOption
    churnNote
    statusReason
    statusNote
    accountExecutive
    upsell {
      executive
      paymentMethods
      date
      note
    }
  }
  ${addressFields}
  ${traceDetailsFields}
`;

export const masterAccountEventObjectFields = gql`
  fragment MasterAccountEventObjectFields on MasterAccountEventObject {
    id
    accountCallbackUrl
    apiKey
    adminEmail
    test
    testAccountId
    prodAccountId
    business {
      mcc
      legalName
      website
      documentNumber
      servicesDescription
    }
    contractTemplateId
    contract {
      envelopeId
      status
      path
      signedDate
    }
    churnedEmails
    allowedDomains
    allowedIPs
    blocked
    iban
    note
    name
    payoutTerms {
      iban
      period
      firstPeriod
      reservePercent
      suspended
      releaseReserve
    }
    promoCode
    status
    traceDetails {
      ...TraceDetailsFields
    }
    updatedAt
    webhookEmails
  }
  ${traceDetailsFields}
`;

export const settlementFields = gql`
  fragment SettlementFields on Settlement {
    accountId
    acquirer
    acquirerFees
    grossVolume
    iban
    id
    payMethod
    netVolume
    periodEndAt
    periodStartAt
    releasedAmount
    reservedAmount
    scheduledAt
    status
    legalName
    storeId
    settleGross
    currency
  }
`;

export const accountInvoiceFields = gql`
  fragment AccountInvoiceFields on AccountInvoice {
    id
    number
    accountId
    period {
      from
      to
    }
    currency
    amount
    tax
    totalAmount
    status
    lineItems {
      name
      quantity
      unitAmount
      unitType
      currency
      amount
    }
    business {
      iban
    }
    paymentMethod
  }
`;

export const paymentFields = gql`
  fragment PaymentFields on Payment {
    id
    accountId
    sequenceId
    subscriptionId
    pointOfSaleId
    masterAccountId
    createdAt
    updatedAt
    amount
    authorizationCode
    storeId
    billingDetails {
      email
      name
      company
      phone
      taxId
      address {
        ...AddressFields
      }
    }
    currency
    customer {
      email
      name
      phone
    }
    description
    descriptor
    livemode
    orderId
    paymentMethod {
      ...PaymentMethodFields
    }
    refundedAmount
    lastRefundAmount
    lastRefundReason
    cancellationReason
    shippingDetails {
      email
      name
      company
      phone
      taxId
      address {
        ...AddressFields
      }
    }
    shop {
      name
      country
    }
    status
    statusCode
    statusMessage
    sessionDetails {
      ...SessionDetailsFields
    }
    traceDetails {
      ...TraceDetailsFields
    }
    nextAction {
      type
      redirectUrl
      mustRedirect
    }
  }
  ${addressFields}
  ${sessionDetailsFields}
  ${traceDetailsFields}
  ${paymentMethodFields}
`;

export const masterAccountFields = gql`
  fragment MasterAccountFields on MasterAccount {
    id
    testAccountId
    prodAccountId
    name
    adminEmail
    iban
    apiKey
    test
    status
    promoCode
    allowedIPs
    payoutTerms {
      iban
      firstPeriod
      period
      reservePercent
      suspended
      releaseReserve
    }
    accountDefaults {
      billingPlan
      language
      fees {
        trxFee
        trxFlatFee
        highRiskFee
        dailyPlatformFee
        dailyExtraSeatsFee
        steps {
          trxFee
          bizumTrxFee
          bizumTrxFlatFee
          trxFlatFee
          from
        }
        extra {
          name
          amount
          type
          region
          masterAccountFee
          paymentMethods
        }
      }
      business {
        mcc
        website
        servicesDescription
      }
    }
    business {
      mcc
      legalName
      website
      documentNumber
      servicesDescription
    }
    createdAt
    updatedAt
    webhookEmails
    churnedEmails
    allowedDomains
    accountCallbackUrl
    requireExternalId
    contractTemplateId
    contract {
      envelopeId
      status
      path
      signedDate
    }
    autoApprove
    blocked
    note
    allowCreateApiKey
    branding {
      accentColor
      bgColor
      logoUrl
      iconUrl
    }
  }
`;

export const userMasterAccountFields = gql`
  fragment UserMasterAccountFields on MasterAccount {
    id
    name
    status
    promoCode
    allowedIPs
    webhookEmails
    churnedEmails
    allowedDomains
    accountCallbackUrl
    requireExternalId
    apiKey
    test
    publicBusinessDetails {
      companyName
      website
      phone
      address {
        ...AddressFields
      }
      supportWebsite
      supportPhone
      supportEmail
    }
    branding {
      accentColor
      bgColor
      logoUrl
      iconUrl
    }
  }
`;

export const documentFields = gql`
  fragment DocumentFields on Document {
    id
    accountId
    type
    status
    statusMessage
    statusCode
    contentType
    createdAt
    updatedAt
    name
    note
    internal
    metadata {
      key
      value
    }
    traceDetails {
      ...TraceDetailsFields
    }
  }
  ${traceDetailsFields}
`;

export const documentEventFields = gql`
  fragment DocumentEventFields on DocumentEvent {
    id
    type
    accountId
    objectId
    objectType
    object {
      id
      accountId
      type
      status
      statusCode
      statusMessage
      contentType
      createdAt
      updatedAt
      note
      name
      internal
      metadata {
        key
        value
      }
      traceDetails {
        ...TraceDetailsFields
      }
    }
    createdAt
  }
  ${traceDetailsFields}
`;

export const contactFields = gql`
  fragment ContactFields on ContactDetails {
    email
    name
    company
    phone
    address {
      ...AddressFields
    }
  }
  ${addressFields}
`;

export const subscriptionFields = gql`
  fragment SubscriptionFields on Subscription {
    id
    accountId
    livemode
    amount
    currency
    description
    planVariantId
    productId
    cancelAtPeriodEnd
    pauseIntervalCount
    pauseAtPeriodEnd
    skipIntervalCount
    currentPeriodStart
    currentPeriodEnd
    trialPeriodDays
    trialPeriodEnd
    customerId
    customer {
      email
      name
      phone
    }
    billingDetails {
      ...ContactFields
    }
    shippingDetails {
      ...ContactFields
    }
    defaultTokenId
    sequenceId
    lastInvoiceId
    lastOrderId
    lastPayment {
      id
      status
      statusMessage
      statusCode
    }
    paymentMethod {
      ...PaymentMethodFields
    }
    nextPaymentAt
    createdAt
    updatedAt
    interval
    intervalCount
    retryCount
    metadata {
      key
      value
    }
    status
    sessionDetails {
      ...SessionDetailsFields
    }
    traceDetails {
      ...TraceDetailsFields
    }
    sessionDetails {
      ...SessionDetailsFields
    }
    callbackUrl
    paymentCallbackUrl
    storeId
    masterAccountId
    metadata {
      key
      value
    }
    allowedPaymentMethods
  }
  ${contactFields}
  ${paymentMethodFields}
  ${traceDetailsFields}
  ${sessionDetailsFields}
`;

export const lastPaymentFields = gql`
  fragment LastPaymentFields on LastPayment {
    id
    status
    statusMessage
    statusCode
  }
`;

export const subscriptionEventObjectFields = gql`
  fragment SubscriptionEventObjectFields on SubscriptionEventObject {
    id
    accountId
    amount
    billingDetails {
      email
      name
      company
      phone
      address {
        ...AddressFields
      }
    }
    cancelAtPeriodEnd
    createdAt
    currency
    currentPeriodEnd
    currentPeriodStart
    customer {
      email
      name
      phone
    }
    description
    interval
    intervalCount
    lastOrderId
    lastPayment {
      ...LastPaymentFields
    }
    livemode
    pauseAtPeriodEnd
    pauseIntervalCount
    paymentMethod {
      ...PaymentMethodFields
    }
    pointOfSaleId
    retryCount
    sequenceId
    shippingDetails {
      email
      name
      company
      phone
      address {
        ...AddressFields
      }
    }
    status
    storeId
    traceDetails {
      ...TraceDetailsFields
    }
    trialPeriodDays
    trialPeriodEnd
    updatedAt
  }
  ${addressFields}
  ${sessionDetailsFields}
  ${traceDetailsFields}
  ${paymentMethodFields}
  ${lastPaymentFields}
`;

export const providerEventObjectFields = gql`
  fragment ProviderEventObjectFields on ProviderEventObject {
    id
    internalId
    processor
    enabled
    test
    paymentMethod
    acquirer
    configurable
    metadata {
      key
      value
    }
    nativeHostedPayment
    rules {
      minAmount
      maxAmount
      shopNameLike
      descriptionLike
    }
    traceDetails {
      ...TraceDetailsFields
    }
  }
  ${traceDetailsFields}
`;

export const webhookFields = gql`
  fragment WebhookFields on Webhook {
    id
    accountId
    url
    enabled
    eventTypes
    secret
    createdAt
    updatedAt
  }
`;

export const chargesExportFields = gql`
  fragment ChargesExportFields on ChargesExport {
    id
    accountId
    status
    expireAt
    statusCode
    statusMessage
  }
`;

export const sftpConfigExportFields = gql`
  fragment SftpConfigExportFields on SftpConfigExport {
    id
    accountId
    host
    port
    username
    path
    columns
    timezone
    enabled
  }
`;

export const storeFields = gql`
  fragment StoreFields on Store {
    id
    accountId
    name
    email
    iban
    phone
    website
    documentNumber
    address {
      ...AddressFields
    }
  }
  ${addressFields}
`;

export const accountBalancesFields = gql`
  fragment AccountBalancesFields on AccountBalances {
    accountId
    currency
    clearedAmount
    createdAt
    outstandingAmount
    totalAmount
    updatedAt
  }
`;

export const adminUserFields = gql`
  fragment AdminUserFields on AdminUser {
    id
    email
    name
    phoneNumber
  }
`;

export const payoutMethodsFields = gql`
  fragment PayoutMethodsFields on AffiliatePayoutMethod {
    id
    title
    details {
      iban
      nif
    }
  }
`;

export const affiliateFields = gql`
  fragment AffiliateFields on Affiliate {
    id
    firstname
    lastname
    email
    company {
      name
      description
    }
    address {
      address
      postal_code
      city
      state
      country {
        name
        code
      }
    }
    meta_data {
      tier
    }
    referral_link {
      link
    }
    affiliate_group_id
    created_at
    payout_methods {
      ...PayoutMethodsFields
    }
  }
  ${payoutMethodsFields}
`;

export const affiliateMerchantFields = gql`
  fragment AffiliateMerchantFields on AffiliateMerchant {
    id
    status
    created_at
    meta_data {
      account_id
      account_status
      company_name
      website
      first_conversion_date
    }
  }
`;

export const affiliateInvoiceFields = gql`
  fragment AffiliateInvoiceFields on AffiliateInvoice {
    id
    number
    amount
    issuedAt
    status
    dueAt
  }
`;

export const blacklistItemFields = gql`
  fragment BlacklistItemFields on BlacklistItem {
    value
    type
    note
    accountId
    createdAt
    updatedAt
    validUntil
  }
`;

export const pointOfSaleFields = gql`
  fragment PointOfSaleFields on PointOfSale {
    id
    accountId
    storeId
    number
    name
    currentPaymentId
    manualAmount
    type
    deviceId
    terminalId
    description
    requireEmail
    requireTaxId
    includeQRCode
    qrCodeText
    fixedAmount
    fixedCurrency
    transactionType
    paymentMethods
    enabled
    pendingSetup
    paymentCallbackUrl
  }
`;

export const adminUserEventFields = gql`
  fragment AdminUserEventFields on AdminUserEvent {
    id
    type
    objectType
    accountId
    objectId
    createdAt
    metadata {
      key
      value
    }
    sessionDetails {
      ...SessionDetailsFields
    }
  }
  ${sessionDetailsFields}
`;

export const codeFields = gql`
  fragment CodeFields on Code {
    id
    type
    enabled
    pointOfSaleId
  }
`;

export const pibisiEventFields = gql`
  fragment PibisiEventFields on PibisiEvent {
    id
    type
    objectId
    objectType
    object {
      id
      entityId
      status
      customerId
      createdAt
      updatedAt
      traceDetails {
        ...TraceDetailsFields
      }
    }
    createdAt
  }
  ${traceDetailsFields}
`;
